// =============================================================================
// MeuDriver — Batch O2: InstructorPackages
// CRUD de pacotes de aulas do instrutor — /instrutor/pacotes
// =============================================================================

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  BookOpen,
  DollarSign,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'
import {
  useInstructorPackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  type InstructorPackage,
} from '@/hooks/useLessonPackages'
import { formatCurrency } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// ---------------------------------------------------------------------------
// Schema de formulário
// ---------------------------------------------------------------------------

const packageSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  lesson_count: z.coerce.number().int().min(1, 'Mínimo 1 aula'),
  price: z.coerce.number().min(0.01, 'Preço deve ser positivo'),
  description: z.string().max(500).optional().or(z.literal('')),
  is_active: z.boolean().default(true),
})

type PackageFormData = z.infer<typeof packageSchema>

// ---------------------------------------------------------------------------
// PackageForm — formulário reutilizável para criar/editar
// ---------------------------------------------------------------------------

interface PackageFormProps {
  defaultValues?: Partial<PackageFormData>
  onSubmit: (data: PackageFormData) => void
  isPending: boolean
  submitLabel: string
}

function PackageForm({ defaultValues, onSubmit, isPending, submitLabel }: PackageFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PackageFormData>({
    resolver: zodResolver(packageSchema),
    defaultValues: {
      name: '',
      lesson_count: 5,
      price: 0,
      description: '',
      is_active: true,
      ...defaultValues,
    },
  })

  const isActive = watch('is_active')

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="pkg-name">Nome do pacote</Label>
        <Input
          id="pkg-name"
          placeholder="Ex: Pacote 10 aulas"
          data-testid="pkg-name-input"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-xs text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="pkg-lessons">
            <BookOpen className="inline h-3.5 w-3.5 mr-1" />
            Quantidade de aulas
          </Label>
          <Input
            id="pkg-lessons"
            type="number"
            min={1}
            data-testid="pkg-lesson-count-input"
            {...register('lesson_count')}
          />
          {errors.lesson_count && (
            <p className="text-xs text-destructive">{errors.lesson_count.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pkg-price">
            <DollarSign className="inline h-3.5 w-3.5 mr-1" />
            Preço (R$)
          </Label>
          <Input
            id="pkg-price"
            type="number"
            step="0.01"
            min={0.01}
            data-testid="pkg-price-input"
            {...register('price')}
          />
          {errors.price && (
            <p className="text-xs text-destructive">{errors.price.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="pkg-description">Descrição (opcional)</Label>
        <Textarea
          id="pkg-description"
          placeholder="Descreva o que está incluso no pacote..."
          rows={3}
          data-testid="pkg-description-input"
          {...register('description')}
        />
        {errors.description && (
          <p className="text-xs text-destructive">{errors.description.message}</p>
        )}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border/60 p-3">
        <div>
          <p className="text-sm font-medium">Pacote ativo</p>
          <p className="text-xs text-muted-foreground">Alunos podem visualizar e comprar</p>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={(v) => setValue('is_active', v)}
          data-testid="pkg-active-switch"
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isPending}
        data-testid="pkg-submit-btn"
      >
        {isPending ? 'Salvando...' : submitLabel}
      </Button>
    </form>
  )
}

// ---------------------------------------------------------------------------
// PackageCard — exibe um pacote com ações de edição e exclusão
// ---------------------------------------------------------------------------

interface PackageCardProps {
  pkg: InstructorPackage
  onEdit: (pkg: InstructorPackage) => void
  onDelete: (pkg: InstructorPackage) => void
}

function PackageCard({ pkg, onEdit, onDelete }: PackageCardProps) {
  const pricePerLesson = pkg.price / pkg.lesson_count

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="rounded-2xl border border-border/60 bg-white p-5 shadow-soft"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-gray-900 truncate">{pkg.name}</p>
            {pkg.is_active ? (
              <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Ativo</Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">Inativo</Badge>
            )}
          </div>
          {pkg.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{pkg.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onEdit(pkg)}
            data-testid="pkg-edit-btn"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => onDelete(pkg)}
            data-testid="pkg-delete-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <span className="flex items-center gap-1 text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" />
          {pkg.lesson_count} aula{pkg.lesson_count !== 1 ? 's' : ''}
        </span>
        <span className="font-bold text-lg text-brand-700">{formatCurrency(pkg.price)}</span>
        <span className="text-xs text-muted-foreground">
          ({formatCurrency(pricePerLesson)}/aula)
        </span>
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// InstructorPackages — página principal
// ---------------------------------------------------------------------------

export default function InstructorPackages() {
  const { user } = useAuth()

  // Busca o instructor_id
  const { data: instructorData } = useQuery({
    queryKey: ['instructor-id', user?.id],
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user!.id)
        .single()
      if (error) throw error
      return data as { id: string }
    },
  })

  const instructorId = instructorData?.id
  const { data: packages, isLoading } = useInstructorPackages(instructorId)
  const createPkg = useCreatePackage()
  const updatePkg = useUpdatePackage()
  const deletePkg = useDeletePackage()

  const [createOpen, setCreateOpen] = useState(false)
  const [editingPkg, setEditingPkg] = useState<InstructorPackage | null>(null)
  const [deletingPkg, setDeletingPkg] = useState<InstructorPackage | null>(null)

  function handleCreate(data: PackageFormData) {
    createPkg.mutate(data, {
      onSuccess: () => setCreateOpen(false),
    })
  }

  function handleUpdate(data: PackageFormData) {
    if (!editingPkg) return
    updatePkg.mutate({ id: editingPkg.id, ...data }, {
      onSuccess: () => setEditingPkg(null),
    })
  }

  function handleDelete() {
    if (!deletingPkg || !instructorId) return
    deletePkg.mutate({ id: deletingPkg.id, instructorId }, {
      onSuccess: () => setDeletingPkg(null),
    })
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <Package className="h-6 w-6 text-brand-600" />
            Meus pacotes
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Crie pacotes de aulas com preço especial para seus alunos.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="gap-1.5"
          data-testid="create-package-btn"
        >
          <Plus className="h-4 w-4" />
          Novo pacote
        </Button>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-28 rounded-2xl" />
        </div>
      ) : !packages || packages.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 rounded-2xl border border-dashed border-border text-center bg-white/60">
          <AlertCircle className="h-10 w-10 text-gray-300" />
          <div>
            <p className="font-semibold text-gray-600">Nenhum pacote ainda</p>
            <p className="text-sm text-muted-foreground mt-1">
              Crie seu primeiro pacote e ofereça condições especiais aos seus alunos.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setCreateOpen(true)}
            className="mt-2 gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Criar pacote
          </Button>
        </div>
      ) : (
        <AnimatePresence mode="popLayout">
          <div className="space-y-3">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onEdit={setEditingPkg}
                onDelete={setDeletingPkg}
              />
            ))}
          </div>
        </AnimatePresence>
      )}

      {/* Dialog: criar */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo pacote</DialogTitle>
          </DialogHeader>
          <PackageForm
            onSubmit={handleCreate}
            isPending={createPkg.isPending}
            submitLabel="Criar pacote"
          />
        </DialogContent>
      </Dialog>

      {/* Dialog: editar */}
      <Dialog open={!!editingPkg} onOpenChange={(o) => !o && setEditingPkg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar pacote</DialogTitle>
          </DialogHeader>
          {editingPkg && (
            <PackageForm
              defaultValues={{
                name: editingPkg.name,
                lesson_count: editingPkg.lesson_count,
                price: editingPkg.price,
                description: editingPkg.description ?? '',
                is_active: editingPkg.is_active,
              }}
              onSubmit={handleUpdate}
              isPending={updatePkg.isPending}
              submitLabel="Salvar alterações"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: confirmar exclusão */}
      <Dialog open={!!deletingPkg} onOpenChange={(o) => !o && setDeletingPkg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar pacote</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            O pacote <strong>{deletingPkg?.name}</strong> será desativado e não aparecerá
            mais para novos alunos. Esta ação pode ser revertida editando o pacote.
          </p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setDeletingPkg(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
              disabled={deletePkg.isPending}
              data-testid="pkg-confirm-delete-btn"
            >
              {deletePkg.isPending ? 'Desativando...' : 'Desativar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
